import type { HelpLocation } from '../../content/types';
import type { HelpAssistantPanelAction } from './actions';
import { areHelpLocationsEqual, clampPanelWidth, createDefaultHelpLocation, isGuideLocationWithStep } from './types';
import type { HelpAssistantPanelState } from './types';

const isValidStepIndex = (stepIndex: number, guideStepCount: number) =>
    Number.isInteger(stepIndex) && stepIndex >= 0 && stepIndex < guideStepCount;

function closeActiveGuide(state: HelpAssistantPanelState): HelpAssistantPanelState {
    if (state.helpLocation.type !== 'guide') {
        if (!state.isOpen) return state;
        return { ...state, isOpen: false };
    }

    const helpLocation = createDefaultHelpLocation();
    if (
        !state.isOpen &&
        state.hoveredStepIndex === null &&
        !state.guideCompact &&
        areHelpLocationsEqual(state.helpLocation, helpLocation)
    ) {
        return state;
    }

    return {
        ...state,
        isOpen: false,
        helpLocation,
        hoveredStepIndex: null,
        guideCompact: false,
    };
}

export function reducer(state: HelpAssistantPanelState, action: HelpAssistantPanelAction): HelpAssistantPanelState {
    switch (action.type) {
        case 'OPEN_HELP': {
            const nextHelpLocation: HelpLocation | undefined = action.documentId
                ? { type: 'document', documentId: action.documentId }
                : undefined;
            if (
                state.isOpen &&
                state.activeMode === 'help' &&
                (!nextHelpLocation || areHelpLocationsEqual(state.helpLocation, nextHelpLocation))
            ) {
                return state;
            }
            return {
                ...state,
                isOpen: true,
                activeMode: 'help',
                hasOpenedOnce: true,
                ...(nextHelpLocation ? { helpLocation: nextHelpLocation } : {}),
            };
        }

        case 'OPEN_ASSISTANT':
            if (state.isOpen && state.activeMode === 'assistant') return state;
            return { ...state, isOpen: true, activeMode: 'assistant', hasOpenedOnce: true };

        case 'RESUME_PANEL': {
            if (state.isOpen) return state;
            // Default to Ask only on a true virgin state: the panel has never
            // been opened in this model's lifetime. Shape alone is ambiguous:
            // a closed root-Help session looks identical to initial state.
            if (!state.hasOpenedOnce) {
                return { ...state, isOpen: true, activeMode: 'assistant', hasOpenedOnce: true };
            }
            return { ...state, isOpen: true };
        }

        case 'CLOSE_PANEL':
            return closeActiveGuide(state);

        case 'NAVIGATE_TO_DOCUMENT': {
            const helpLocation: HelpLocation = { type: 'document', documentId: action.documentId };
            if (state.activeMode === 'help' && areHelpLocationsEqual(state.helpLocation, helpLocation)) return state;
            return { ...state, activeMode: 'help', helpLocation };
        }

        case 'NAVIGATE_TO_PATH': {
            const helpLocation: HelpLocation = { type: 'browse', path: [...action.path] };
            if (state.activeMode === 'help' && areHelpLocationsEqual(state.helpLocation, helpLocation)) return state;
            return { ...state, activeMode: 'help', helpLocation };
        }

        case 'NAVIGATE_TO_CONTACT': {
            const helpLocation: HelpLocation = { type: 'contact' };
            if (state.activeMode === 'help' && areHelpLocationsEqual(state.helpLocation, helpLocation)) return state;
            return { ...state, activeMode: 'help', helpLocation };
        }

        case 'START_GUIDE': {
            const helpLocation: HelpLocation = { type: 'guide', guideId: action.guideId, stepIndex: 0 };
            if (
                state.isOpen &&
                state.activeMode === 'help' &&
                state.hoveredStepIndex === null &&
                !state.guideCompact &&
                areHelpLocationsEqual(state.helpLocation, helpLocation)
            ) {
                return state;
            }
            return {
                ...state,
                isOpen: true,
                activeMode: 'help',
                helpLocation,
                hoveredStepIndex: null,
                guideCompact: false,
                hasOpenedOnce: true,
            };
        }

        case 'SET_GUIDE_STEP': {
            if (state.helpLocation.type !== 'guide') return state;
            if (!isValidStepIndex(action.stepIndex, action.guideStepCount)) return state;
            if (
                state.hoveredStepIndex === null &&
                isGuideLocationWithStep(state.helpLocation, state.helpLocation.guideId, action.stepIndex)
            ) {
                return state;
            }
            return {
                ...state,
                helpLocation: { type: 'guide', guideId: state.helpLocation.guideId, stepIndex: action.stepIndex },
                hoveredStepIndex: null,
            };
        }

        case 'SET_HOVERED_STEP':
            if (action.stepIndex !== null) {
                if (state.helpLocation.type !== 'guide') return state;
                if (!isValidStepIndex(action.stepIndex, action.guideStepCount ?? 0)) return state;
            }
            if (state.hoveredStepIndex === action.stepIndex) return state;
            return { ...state, hoveredStepIndex: action.stepIndex };

        case 'SET_GUIDE_COMPACT':
            if (state.guideCompact === action.compact) return state;
            return { ...state, guideCompact: action.compact };

        case 'EXIT_GUIDE': {
            if (state.helpLocation.type !== 'guide') {
                if (state.hoveredStepIndex === null && !state.guideCompact) return state;
                return {
                    ...state,
                    hoveredStepIndex: null,
                    guideCompact: false,
                };
            }
            // Exit the tour but keep the panel open at the help root. Closing
            // the entire Help Center on tour-X is unexpected; the panel's own
            // X uses CLOSE_PANEL for that.
            return {
                ...state,
                helpLocation: createDefaultHelpLocation(),
                hoveredStepIndex: null,
                guideCompact: false,
            };
        }

        case 'SET_PANEL_WIDTH': {
            const next = clampPanelWidth(action.width);
            if (state.panelWidth === next) return state;
            return { ...state, panelWidth: next };
        }

        default: {
            const _exhaustiveCheck: never = action;
            return _exhaustiveCheck;
        }
    }
}
